import React, { useContext, useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import Swal from 'sweetalert2';

import { AuthContext } from "../contexts/auth";
import PopoverComponentCustom from "../components/popover_component";
import Footer from "../components/footer";
import { getCompanySettings } from "../services/api_settings";

import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";

import V2PairOrgUnit from "../components/v2_pair_org_unit";

import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { AcademicCapIcon } from "@heroicons/react/24/outline";

import CopyDailyShift from "../components/copy_daily_shift";
import CopyWeeklyShift from "../components/copy_weekly_shift";

import CreateDailyShiftForm from "../components/create_daily_shift_form";
import CreateWeeklyShiftForm from "../components/create_weekly_shift_form";

export default function V2ShiftManagement() {
  const { token, profile } = useContext(AuthContext);

  const [allowHiddenBreaks, setAllowHiddenBreaks] = useState(false);

  const [mode, setMode] = useState("scratch"); // "copy" | "scratch"

  // tipo turno
  const [type, setType] = useState(null); // null = nessuna selezione
  const [typeLocked, setTypeLocked] = useState(false);

  // OU destinazione (turno creato)
  const [selectedOu, setSelectedOu] = useState(null);
  const [destOuLocked, setDestOuLocked] = useState(true);

  const [resetKey, setResetKey] = useState(0);

  const [weeklyHasDailyWH, setWeeklyHasDailyWH] = useState(false);
  const [weeklyClearDailyWHSignal, setWeeklyClearDailyWHSignal] = useState(0);
  const [location] = useLocation();

  const typeLabel = type === "daily" ? "Giornaliero" : type === "weekly" ? "Settimanale" : "";
  const TypeIcon = type === "daily" ? AccessTimeOutlinedIcon : CalendarTodayOutlinedIcon;

  const isProfileComplete = profile?.has_complete_profile;
  const showProfileWarning =
    profile?.customer_type === "company" &&
    !isProfileComplete &&
    profile?.company?.email === profile?.the_real_user_email;

  useEffect(() => {
    const url = new URL(window.location.href);
    const qType = url.searchParams.get("type");
    const qMode = url.searchParams.get("mode");

    const shouldApply =
      (qType === "daily" || qType === "weekly") ||
      (qMode === "scratch" || qMode === "copy");

    if (qType === "daily" || qType === "weekly") {
      setType(qType);
      setTypeLocked(true);
    }

    if (qMode === "scratch" || qMode === "copy") {
      setMode(qMode);
    } else if (shouldApply) {
      setMode("scratch");
    }

    if (shouldApply) {
      url.searchParams.delete("type");
      url.searchParams.delete("mode");
      window.history.replaceState({}, "", url.pathname + url.search);
    }
  }, []);

  const handlePickType = (next) => {
    setType(next);
    setTypeLocked(true);
  };

  useEffect(() => {
    setDestOuLocked(true);
  }, [selectedOu]);

  // settings
  useEffect(() => {
    if (!token) return;

    let cancelled = false;
    (async () => {
      try {
        const settings = await getCompanySettings(token, "payrolls");
        const allowHidden = Boolean(settings?.allow_hidden_breaks ?? false);
        if (!cancelled) setAllowHiddenBreaks(allowHidden);
      } catch {
        if (!cancelled) setAllowHiddenBreaks(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  // reset completo dopo creazione turno (daily/weekly)
  const resetForm = () => {
    setMode("scratch");

    setType(null);
    setTypeLocked(false);

    setSelectedOu(null);
    setDestOuLocked(true);
    setResetKey((k) => k + 1);
  };

  const handleChangeType = () => {
    // chiudi la card (mostra picker)
    setTypeLocked(false);

    // nascondi completamente il flow sotto
    setType(null);

    // reset flow
    setMode("scratch");
    setResetKey((k) => k + 1);
  };

  const isWeeklyScratch = type === "weekly" && mode === "scratch";

  const ouIdOf = (ou) => (ou && typeof ou === "object" ? ou.id : ou) ?? null;

  const confirmOuChangeIfNeeded = async (nextOu) => {
    if (!isWeeklyScratch || !weeklyHasDailyWH) return true;

    const prevId = ouIdOf(selectedOu);
    const nextId = ouIdOf(nextOu);

    // se non cambia davvero, niente swal
    if (prevId === nextId) return true;

    const res = await Swal.fire({
      icon: "warning",
      title: "Cambiare unità?",
      html: `
        Hai già associato uno o più <b>turni giornalieri</b> ai giorni.<br/>
        Cambiando unità verranno <b>rimossi</b> i turni giornalieri selezionati.<br/><br/>
        Vuoi continuare?
      `,
      showCancelButton: true,
      cancelButtonText: "Annulla",
      confirmButtonText: "Sì, continua",
      reverseButtons: false,
      focusCancel: true,
    });

    if (!res.isConfirmed) return false;

    // pulizia nel figlio (solo working_hour)
    setWeeklyClearDailyWHSignal((k) => k + 1);

    return true;
  };

  return (
    <div className="flex flex-col min-h-screen bg-gray-50/40">
      <PopoverComponentCustom title="Crea Turni" />

      {showProfileWarning && (
        <div className="w-full">
          <div className="bg-orange-100 text-orange-600 p-4 mb-6">
            <p className="text-center text-sm font-semibold">
              Il profilo non è completo. <br />
              Compila i campi mancanti{" "}
              <Link to="/profilo/fatturazione" className="text-indigo-600 hover:text-indigo-800 underline">
                cliccando qui
              </Link>
            </p>
          </div>
        </div>
      )}

      <main className="flex-grow mt-10 pb-8">
        <div className="pb-8 flex-grow">
          <div className="mx-auto w-full max-w-[1600px] px-8">
            <div>
              <h1 className="text-md font-semibold uppercase text-indigo-800">Crea turno</h1>
              <p className="text-sm text-gray-500">Crea un turno da zero oppure copia un turno esistente</p>
            </div>

            <div className="mt-6 space-y-5">
              {/* HEADER CARDS */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* A) Tipo */}                
                <section className="rounded-xl border border-gray-200 bg-white shadow-md overflow-visible">
                  <div className="flex h-full">
                    {/* LEFT HEADER (verticale) */}
                    <div className="w-28 bg-indigo-100 px-4 py-4 flex flex-col justify-center gap-3 rounded-l-xl">
                      <div className="text-sm font-semibold uppercase text-indigo-800 leading-tight">
                        Tipo<br />turno
                      </div>

                      {typeLocked ? (
                        <button
                          type="button"
                          onClick={handleChangeType}
                          className="text-xs font-semibold text-indigo-700 hover:text-indigo-900 text-left"
                        >
                          Cambia
                        </button>
                      ) : (
                        <div className="text-transparent">.</div>
                      )}
                    </div>

                    {/* RIGHT CONTENT */}
                    <div className="flex-1 p-3">
                      {typeLocked ? (
                        <div className="gap-4">
                          <div className="flex items-center gap-2 rounded-xl bg-indigo-50 p-2 text-sm font-semibold text-indigo-800 border border-indigo-600 w-[60%] min-w-0 mb-2 uppercase">
                            {type ? (
                              <TypeIcon className="h-5 w-5 text-indigo-700 shrink-0" />
                            ) : (
                              <AccessTimeOutlinedIcon className="h-5 w-5 text-indigo-700 shrink-0" />
                            )}
                            <span className="truncate">
                              {type ? typeLabel : "Generico"}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 leading-snug text-justify">
                            {type === "daily" &&
                              "Il turno sarà composto da una o più fasce orarie all'interno della stessa giornata."}
                            {type === "weekly" &&
                              "Il turno sarà strutturato su più giorni della settimana con fasce orarie specifiche."}
                            {!type && "Seleziona il tipo di turno."}
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="inline-flex rounded-xl shadow-sm overflow-hidden border border-indigo-600 mb-2">
                            <button
                              type="button"
                              onClick={() => handlePickType("daily")}
                              className={`px-4 py-2 text-sm font-semibold transition flex items-center gap-2 focus:outline-none uppercase
                                ${type === "daily" ? "bg-indigo-600 text-white" : "bg-indigo-50 text-indigo-700 hover:bg-indigo-100"}`}
                            >
                              <AccessTimeOutlinedIcon sx={{ fontSize: 18 }} />
                              Giornaliero
                            </button>

                            <button
                              type="button"
                              onClick={() => handlePickType("weekly")}
                              className={`px-4 py-2 text-sm font-semibold transition border-l flex items-center gap-2 focus:outline-none uppercase
                                ${type === "weekly" ? "bg-indigo-600 text-white border-indigo-600" : "bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border-indigo-200"}`}
                            >
                              <CalendarTodayOutlinedIcon sx={{ fontSize: 18 }} />
                              Settimanale
                            </button>
                          </div>
                          <div className="text-xs text-gray-500 leading-snug text-justify align-bottom mt-4">
                            Seleziona la tipologia di turno che vuoi creare.
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </section>

                {/* B) OU Destinazione */}
                <section className="rounded-xl border border-gray-200 bg-white shadow-md overflow-visible">
                  <div className="flex h-full min-w-0">
                    {/* LEFT HEADER (verticale) */}
                    <div className="w-28 bg-indigo-100 px-4 py-4 flex flex-col justify-center gap-3 rounded-l-xl">
                      <div className="text-sm font-semibold uppercase text-indigo-800 leading-tight">
                        Unità<br />collegata
                      </div>

                      <button
                        type="button"
                        onClick={async () => {
                          const isWeeklyScratch = type === "weekly" && mode === "scratch";

                          if (isWeeklyScratch && weeklyHasDailyWH) {
                            const res = await Swal.fire({
                              icon: "warning",
                              title: "Cambiare unità?",
                              html: `
                                Hai già associato uno o più <b>turni giornalieri</b> ai giorni.<br/>
                                Cambiando unità verranno <b>rimossi</b> i turni giornalieri selezionati.<br/><br/>
                                Vuoi continuare?
                              `,
                              showCancelButton: true,
                              cancelButtonText: "Annulla",
                              confirmButtonText: "Sì, continua",
                              reverseButtons: false,
                              focusCancel: true,
                            });

                            if (!res.isConfirmed) return;
                            
                            setWeeklyClearDailyWHSignal((k) => k + 1);
                            Swal.fire({
                              icon: "info",
                              title: "Turni giornalieri rimossi",
                              timer: 1200,
                              showConfirmButton: false,
                            });
                          }

                          setDestOuLocked(false);
                        }}
                        className="text-xs font-semibold text-indigo-700 hover:text-indigo-900 text-left"
                      >
                        Cambia
                      </button>
                    </div>

                    {/* RIGHT CONTENT */}
                    <div className="flex-1 min-w-0 px-5 py-4">
                      {destOuLocked ? (
                        <div className="gap-4 min-w-0">
                          <div className="flex items-center gap-2 rounded-xl bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-800 border border-indigo-600 w-3/4 min-w-0 mb-2 uppercase">
                            <AcademicCapIcon className="h-5 w-5 text-indigo-700 shrink-0" />
                            <span className="truncate min-w-0 block">
                              {selectedOu ? selectedOu.name : "Generico"}
                            </span>
                          </div>

                          <div className="text-xs text-gray-500 leading-snug text-justify">
                            Selezionando l'unità, il conteggio delle ore del turno verrà incluso nel totale delle ore assegnate sulla specifica unità.
                          </div>
                        </div>
                      ) : (
                        <V2PairOrgUnit
                          label="Selezione unità"
                          helperText="Se selezionata, il nuovo turno verrà associato a questa unità."
                          value={selectedOu}
                          onChange={async (ou) => {
                            const ok = await confirmOuChangeIfNeeded(ou);
                            if (!ok) return;

                            setSelectedOu(ou);
                            setDestOuLocked(true);
                          }}
                          includeGenericOption
                          genericLabel="Nessuna unità (Generico)"
                        />
                      )}
                    </div>
                  </div>
                </section>
              </div>

              {/* MODALITÀ + FLOW */}
              {type && (
                <section className="rounded-xl border border-gray-200 bg-white shadow-sm">
                  <div className="text-sm font-semibold uppercase text-indigo-800 px-5 py-2 bg-indigo-100 rounded-t-xl">
                    Come vuoi crearlo?
                  </div>

                  <div className="mt-3 px-5 flex justify-center">
                    <div className="flex w-3/4 rounded-xl border border-indigo-100 bg-indigo-50 p-1">
                      <button
                        type="button"
                        onClick={() => setMode("scratch")}
                        className={`uppercase flex-1 px-4 py-2 text-sm font-semibold transition flex items-center justify-center gap-2 rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600
                          ${mode === "scratch" ? "bg-indigo-600/90 text-white shadow-sm ring-1 ring-indigo-200" : "text-indigo-600 hover:bg-white/60"}`}
                      >
                        <AddCircleOutlineIcon sx={{ fontSize: 18 }} />
                        Crea nuovo
                      </button>
                      <button
                        type="button"
                        onClick={() => setMode("copy")}
                        className={`uppercase flex-1 px-4 py-2 text-sm font-semibold transition flex items-center justify-center gap-2 rounded-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600
                          ${mode === "copy" ? "bg-indigo-600/90 text-white shadow-sm ring-1 ring-indigo-200" : "text-indigo-600 hover:bg-white/60"}`}
                      >
                        <ContentCopyOutlinedIcon sx={{ fontSize: 18 }} />
                        Copia da esistente
                      </button>
                    </div>
                  </div>

                  {/* COPY DAILY */}
                  {mode === "copy" && type === "daily" && (
                    <CopyDailyShift key={`copy-daily-${resetKey}`} token={token} selectedOu={selectedOu} onSuccess={resetForm} />
                  )}

                  {/* COPY WEEKLY */}
                  {mode === "copy" && type === "weekly" && (
                    <CopyWeeklyShift key={`copy-weekly-${resetKey}`} token={token} selectedOu={selectedOu} onSuccess={resetForm} />
                  )}

                  {/* CREATE DAILY */}
                  {mode === "scratch" && type === "daily" && (
                    <CreateDailyShiftForm
                      key={`create-daily-${resetKey}`}
                      token={token}
                      selectedOu={selectedOu}
                      allowHiddenBreaks={allowHiddenBreaks}
                      onSuccess={resetForm}
                    />
                  )}

                  {/* CREATE WEEKLY */}
                  {mode === "scratch" && type === "weekly" && (
                    <CreateWeeklyShiftForm
                      key={`create-weekly-${resetKey}`}
                      token={token}
                      selectedOu={selectedOu}
                      allowHiddenBreaks={allowHiddenBreaks}
                      onSuccess={resetForm}
                      onWeeklyHasDailyWorkingHoursChange={setWeeklyHasDailyWH}
                      weeklyClearDailyWorkingHoursSignal={weeklyClearDailyWHSignal}
                    />
                  )}
                </section>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}